import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import { usePermisos } from '../context/PermisosContext';
import Notificaciones from './Notificaciones';
import api from '../services/api';
import logoCauFabrica from '../pages/caucared/logo-caucared.png';

/**
 * CAUCARED, la marca hermana, vive en /caucared/... Al entrar cambia TODO lo que se ve —
 * el menú, el color (el verde de su logo en vez de cian), el fondo, el logo y una franja fija arriba— para
 * que no haya forma de confundir en cuál empresa se está facturando. Víctor lo pidió así:
 * «bien diferenciado, para no confundirse». Su plata va en tablas aparte y no toca la
 * contabilidad de LinkUs.
 */
const MENU_CAU = [
  { grupo: 'CAUCARED', icon: '📝', label: 'Cotizaciones',  ruta: '/caucared/cotizaciones' },
  { grupo: 'CAUCARED', icon: '🧾', label: 'Facturas',      ruta: '/caucared/facturas' },
  { grupo: 'CAUCARED', icon: '💼', label: 'Cartera',       ruta: '/caucared/cartera' },
  { grupo: 'CAUCARED', icon: '🏢', label: 'Clientes',      ruta: '/caucared/clientes' },
  { grupo: 'CAUCARED', icon: '📈', label: 'Reporte',       ruta: '/caucared/reporte' },
  { grupo: 'CAUCARED', icon: '⚙',  label: 'Configuración', ruta: '/caucared/configuracion' },
];
const TEMA = {
  linkus:   { acento: '#00E5FF', suave: 'rgba(0,229,255,0.1)',  borde: 'rgba(0,229,255,0.3)',  fondo: '#0A0E1A', barra: '#0D1120' },
  caucared: { acento: '#2FBF71', suave: 'rgba(47,191,113,0.12)', borde: 'rgba(47,191,113,0.35)', fondo: '#07120D', barra: '#0B1A12' },
};
// El logo de CAUCARED se pide una vez por sesión, no en cada pantalla
let logoCauGuardado;

/**
 * El menú, agrupado por para qué se entra.
 *
 * Eran 22 entradas seguidas, todas al mismo nivel, y encontrar algo era leerlas todas.
 * Ahora van en cinco bloques con un título encima, ordenados de lo que se usa todos los
 * días a lo que se toca una vez al año.
 *
 * Los grupos NO se pliegan a propósito: plegarlos añadiría un clic cada vez que se entra
 * a cualquier sitio, todos los días, a cambio de una lista más corta. No compensa.
 *
 * «Equipos», «Infraestructura», «Ingresos/Egresos» y «Caja diaria» ya no están: los dos
 * primeros eran otra lista de los mismos aparatos y ahora viven en Red → Equipos; los dos
 * últimos se fundieron en Dinero. Sus rutas siguen existiendo y redirigen, por si alguien
 * las tiene guardadas.
 */
const MENU = [
  // Lo de todos los días
  { grupo: 'Día a día', icon: '◉', label: 'Dashboard',   ruta: '/dashboard', modulo: 'dashboard' },
  { grupo: 'Día a día', icon: '👥', label: 'Clientes',    ruta: '/clientes',  modulo: 'clientes' },
  { grupo: 'Día a día', icon: '💳', label: 'Cartera',     ruta: '/cartera',   modulo: 'cartera' },
  { grupo: 'Día a día', icon: '🎫', label: 'Tickets',     ruta: '/tickets',   modulo: 'tickets' },
  { grupo: 'Día a día', icon: '📅', label: 'Agenda',      ruta: '/agenda',    modulo: 'agenda' },

  // La plata
  { grupo: 'Dinero', icon: '💰', label: 'Dinero',   ruta: '/dinero',   modulo: 'transacciones' },
  { grupo: 'Dinero', icon: '📊', label: 'Reportes', ruta: '/reportes', modulo: 'reportes' },

  // Lo que se vende
  { grupo: 'Ventas y tienda', icon: '🛒', label: 'Ventas y Servicios', ruta: '/ventas',       modulo: 'ventas' },
  { grupo: 'Ventas y tienda', icon: '👔', label: 'Vendedores',         ruta: '/vendedores',   modulo: 'ventas' },
  { grupo: 'Ventas y tienda', icon: '📋', label: 'Cotizaciones',       ruta: '/cotizaciones', modulo: 'cotizaciones' },
  { grupo: 'Ventas y tienda', icon: '📥', label: 'Pedidos Web',        ruta: '/pedidos-web',  modulo: 'ventas' },
  { grupo: 'Ventas y tienda', icon: '📦', label: 'Catálogo',           ruta: '/catalogo',     modulo: 'catalogo' },

  // Los fierros
  { grupo: 'Red', icon: '🕸️', label: 'Red de fibra', ruta: '/red', modulo: 'equipos_red' },
  { grupo: 'Red', icon: '🌐', label: 'Proveedores de Red', ruta: '/proveedores-red', modulo: 'proveedores_red' },
  // soloSuper: tocan el funcionamiento del sistema, no el negocio del día a día.
  // MikroTik controla el internet de los clientes y Configuración guarda las claves del
  // correo y de Mercado Pago, además de la descarga y restauración de la base. El
  // administrador maneja todo lo demás: clientes, cartera, catálogo y ventas.
  { grupo: 'Red', icon: '📡', label: 'MikroTik', ruta: '/mikrotik', modulo: 'mikrotik', soloSuper: true },

  // Lo que se configura una vez
  { grupo: 'Ajustes', icon: '📋', label: 'Planes',        ruta: '/planes',        modulo: 'planes' },
  { grupo: 'Ajustes', icon: '🤝', label: 'Revendedores',  ruta: '/revendedores',  modulo: 'revendedores' },
  { grupo: 'Ajustes', icon: '📲', label: 'WhatsApp',      ruta: '/whatsapp',      modulo: 'whatsapp' },
  { grupo: 'Ajustes', icon: '👤', label: 'Usuarios',      ruta: '/usuarios',      modulo: 'usuarios' },
  { grupo: 'Ajustes', icon: '⚙',  label: 'Configuración', ruta: '/configuracion', modulo: 'configuracion', soloSuper: true },
  { grupo: 'Ajustes', icon: '❓', label: 'Ayuda',         ruta: '/ayuda',         modulo: 'ayuda' },
];

export default function Layout({ children }) {
  const { usuario, logout } = useAuth();
  const { empresa, logoSrc } = useEmpresa();
  const { puede } = usePermisos();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [esMobil, setEsMobil] = useState(false);
  const navegar = (ruta) => { navigate(ruta); setMenuAbierto(false); };
  const enCau = location.pathname.startsWith('/caucared');
  const tema = TEMA[enCau ? 'caucared' : 'linkus'];
  const [cau, setCau] = useState(logoCauGuardado || null);

  useEffect(() => {
    if (!enCau || logoCauGuardado) return;
    api.get('/api/caucared/config').then(({ data }) => {
      logoCauGuardado = { nombre: data.nombre, logo: data.logo_base64 ? `data:${data.logo_mime || 'image/png'};base64,${data.logo_base64}` : logoCauFabrica };
      setCau(logoCauGuardado);
    }).catch(() => {});
  }, [enCau]);

  useEffect(() => {
    const check = () => setEsMobil(window.innerWidth < 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const esSuper = usuario?.rol === 'superadmin';
  // Los módulos del sistema solo los ve el superadministrador, aunque la tabla de
  // permisos diga otra cosa: el backend los bloquea igual y mostrarlos solo llevaría a
  // entrar a una pantalla que va a dar error.
  const puedeCau = ['admin', 'superadmin'].includes(usuario?.rol);
  const menuVisible = enCau ? MENU_CAU
    : MENU.filter(item => item.soloSuper ? esSuper : (puede(item.modulo) || esSuper));
  const nombreEmpresa = enCau ? (cau?.nombre || 'CAUCARED Soluciones Informáticas') : (empresa?.nombre || 'LINKUS TELECOMUNICACIONES');
  const logoActual = enCau ? (cau?.logo || logoCauFabrica) : logoSrc;

  // Las dos empresas, lado a lado. La activa se ve encendida con su propio color.
  const selector = puedeCau && (
    <div style={{ display:'flex', gap:'4px', background:'rgba(255,255,255,0.05)', borderRadius:'10px', padding:'3px' }}>
      {[['linkus', 'LinkUs', '/dashboard'], ['caucared', 'CAUCARED', '/caucared/cotizaciones']].map(([id, txt, ruta]) => {
        const activa = (id === 'caucared') === enCau;
        const t = TEMA[id];
        return (
          <button key={id} onClick={() => { if (!activa) navegar(ruta); }}
            style={{ flex:1, border: activa ? `1px solid ${t.borde}` : '1px solid transparent', background: activa ? t.suave : 'transparent',
              color: activa ? t.acento : 'rgba(255,255,255,0.45)', borderRadius:'8px', padding:'7px 6px', fontSize:'12px',
              fontWeight: 800, letterSpacing:'0.5px', cursor: activa ? 'default' : 'pointer' }}>
            {txt}
          </button>
        );
      })}
    </div>
  );

  // Franja fija de CAUCARED: se ve siempre, en cualquier pantalla de la marca hermana
  const franja = enCau && (
    <div style={{ background: tema.acento, color:'#03170C', padding:'7px 16px', fontSize:'12px', fontWeight:700,
      display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap', borderRadius: esMobil ? 0 : '10px', marginBottom: esMobil ? 0 : '18px' }}>
      <span style={{ fontWeight:900, letterSpacing:'1px' }}>CAUCARED</span>
      <span style={{ opacity:0.8, fontWeight:600 }}>Soluciones Informáticas · facturación aparte, no entra en la contabilidad de LinkUs</span>
    </div>
  );

  // Los grupos se arman de lo que quedó visible, no de la lista completa: si a un usuario
  // le quitaron todos los permisos de un bloque, no debe verse el título de un bloque
  // vacío. El orden es el de MENU, no alfabético.
  const grupos = menuVisible.reduce((acc, item) => {
    const ultimo = acc[acc.length - 1];
    if (ultimo && ultimo.nombre === item.grupo) ultimo.items.push(item);
    else acc.push({ nombre: item.grupo, items: [item] });
    return acc;
  }, []);

  if (esMobil) {
    return (
      <div style={{ minHeight:'100vh', background: tema.fondo, fontFamily:"'Sora','Segoe UI',sans-serif", color:'#fff' }}>
        <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:1000, background: tema.barra, borderBottom: enCau ? `2px solid ${tema.acento}` : '1px solid rgba(255,255,255,0.1)', padding:'10px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
          <button onClick={() => setMenuAbierto(!menuAbierto)}
            style={{ background: tema.suave, border:`1px solid ${tema.borde}`, borderRadius:'8px', width:'40px', height:'40px', color: tema.acento, fontSize:'20px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {menuAbierto ? '✕' : '☰'}
          </button>
          <span style={{ flex:1, minWidth:0, fontSize:'13px', fontWeight:'800', letterSpacing:'0.5px', color: enCau ? tema.acento : '#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nombreEmpresa}</span>
          <Notificaciones />
        </div>
        {menuAbierto && (
          <div style={{ position:'fixed', top:'61px', left:0, right:0, bottom:0, zIndex:999, background: tema.barra, overflowY:'auto', borderTop:'1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ padding:'8px 12px' }}>
              {selector && <div style={{ padding:'8px 4px 4px' }}>{selector}</div>}
              {grupos.map((g, i) => (
                <div key={g.nombre + i}>
                  <div style={{ fontSize:'11px', fontWeight:'700', letterSpacing:'0.7px',
                    textTransform:'uppercase', color:'rgba(255,255,255,0.28)',
                    padding:'0 16px', marginTop: i === 0 ? '6px' : '18px', marginBottom:'6px' }}>
                    {g.nombre}
                  </div>
                  {g.items.map((item) => {
                    const activo = location.pathname === item.ruta;
                    return (
                      <div key={item.ruta} onClick={() => navegar(item.ruta)}
                        style={{ display:'flex', alignItems:'center', gap:'14px', padding:'14px 16px', borderRadius:'10px', marginBottom:'4px', background: activo ? tema.suave : 'transparent', color: activo ? tema.acento : 'rgba(255,255,255,0.7)', fontSize:'15px', cursor:'pointer', fontWeight: activo ? '600' : '400', borderLeft: activo ? `3px solid ${tema.acento}` : '3px solid transparent' }}>
                        <span style={{ fontSize:'20px', minWidth:'28px', textAlign:'center' }}>{item.icon}</span>
                        <span>{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', marginTop:'12px', paddingTop:'12px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', marginBottom:'10px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'linear-gradient(135deg,#00E5FF,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', fontWeight:'700' }}>
                    {usuario?.nombre?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize:'14px', fontWeight:'600' }}>{usuario?.nombre}</div>
                    <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.4)', textTransform:'capitalize' }}>{usuario?.rol}</div>
                  </div>
                </div>
                <button onClick={logout} style={{ width:'100%', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'12px', color:'#FCA5A5', fontSize:'14px', cursor:'pointer' }}>
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        )}
        <div style={{ paddingTop:'61px' }}>
          {franja}
          <main style={{ padding:'16px', minHeight:'calc(100vh - 61px)' }}>{children}</main>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...s.container, background: tema.fondo }}>
      <aside style={{ ...s.sidebar, ...(enCau ? { borderRight:`1px solid ${tema.borde}`, background:'rgba(47,191,113,0.04)' } : {}) }}>
        <div style={s.logo} onClick={() => navigate(enCau ? '/caucared/cotizaciones' : '/dashboard')}>
          {logoActual
            ? <img src={logoActual} alt="logo" style={s.logoImg} />
            : (
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="14" stroke="#00E5FF" strokeWidth="2"/>
                <path d="M10 16 Q16 8 22 16 Q16 24 10 16Z" fill="#00E5FF" opacity="0.8"/>
                <circle cx="16" cy="16" r="3" fill="#fff"/>
              </svg>
            )
          }
          <span style={{ ...s.logoText, color: enCau ? tema.acento : '#fff' }}>{nombreEmpresa}</span>
        </div>
        {selector && <div style={{ padding:'14px 14px 0' }}>{selector}</div>}
        <nav style={s.nav}>
          {grupos.map((g, i) => (
            <div key={g.nombre + i}>
              <div style={{ ...s.grupo, marginTop: i === 0 ? '2px' : '15px' }}>{g.nombre}</div>
              {g.items.map((item) => {
                const activo = location.pathname === item.ruta;
                return (
                  <div key={item.ruta} onClick={() => navigate(item.ruta)}
                    style={{ ...s.navItem, ...(activo ? { background: tema.suave, color: tema.acento, fontWeight:'600' } : {}) }}>
                    <span style={{ minWidth:'20px', textAlign:'center' }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div style={s.footer}>
          <div style={s.notifRow}><Notificaciones /></div>
          <div style={s.usuarioInfo}>
            <div style={s.avatar}>{usuario?.nombre?.[0]?.toUpperCase()}</div>
            <div>
              <div style={s.nombre}>{usuario?.nombre}</div>
              <div style={s.rol}>{usuario?.rol}</div>
            </div>
          </div>
          <button onClick={logout} style={s.salir}>Salir</button>
        </div>
      </aside>
      <main style={s.main}>{franja}{children}</main>
    </div>
  );
}

const s = {
  container: { display:'flex', minHeight:'100vh', background:'#0A0E1A', fontFamily:"'Sora','Segoe UI',sans-serif", color:'#fff' },
  sidebar: { width:'240px', minWidth:'240px', background:'rgba(255,255,255,0.03)', borderRight:'1px solid rgba(255,255,255,0.08)', display:'flex', flexDirection:'column', padding:'24px 0' },
  logo: { display:'flex', alignItems:'center', gap:'10px', padding:'0 20px 24px', borderBottom:'1px solid rgba(255,255,255,0.08)', cursor:'pointer' },
  logoImg: { width:'32px', height:'32px', objectFit:'contain', borderRadius:'6px' },
  logoText: { fontSize:'15px', fontWeight:'800', letterSpacing:'1px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  nav: { flex:1, padding:'16px 12px', display:'flex', flexDirection:'column', gap:'4px', overflowY:'auto' },
  // El título del bloque: se lee al buscar, no compite con las opciones
  grupo: { fontSize:'10px', fontWeight:'700', letterSpacing:'0.8px', textTransform:'uppercase', color:'rgba(255,255,255,0.25)', padding:'0 12px', marginBottom:'5px' },
  navItem: { display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'8px', fontSize:'13px', cursor:'pointer', color:'rgba(255,255,255,0.5)' },
  navActivo: { background:'rgba(0,229,255,0.1)', color:'#00E5FF', fontWeight:'600' },
  footer: { padding:'16px 20px', borderTop:'1px solid rgba(255,255,255,0.08)' },
  notifRow: { display:'flex', justifyContent:'flex-end', marginBottom:'12px' },
  usuarioInfo: { display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px' },
  avatar: { width:'34px', height:'34px', borderRadius:'50%', background:'linear-gradient(135deg,#00E5FF,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:'700', flexShrink:0 },
  nombre: { fontSize:'13px', fontWeight:'600' },
  rol: { fontSize:'11px', color:'rgba(255,255,255,0.4)', textTransform:'capitalize' },
  salir: { width:'100%', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'8px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer' },
  main: { flex:1, padding:'32px', overflowY:'auto', overflowX:'hidden' },
};
